import { Pill } from "@/components/ui/pill";
import type { SequenceOrderResult } from "@/types/sequence.types";

interface Props {
  orders: SequenceOrderResult[];
  /** Código/rótulo da OP por id — a simulação só conhece o id. */
  labelOf: (orderId: string) => string;
  /** Âncora da simulação: os tempos do motor são offsets a partir dela. */
  anchor: Date | null;
}

const fmtClock = (anchor: Date | null, offsetS: number | null): string => {
  if (offsetS == null) return "—";
  if (!anchor) return `${Math.round(offsetS / 60)} min`;
  return new Date(anchor.getTime() + offsetS * 1000).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Atraso com cor + dot + rótulo (nunca só cor). */
function LatenessCell({ order }: { order: SequenceOrderResult }) {
  if (!order.completed) {
    return (
      <Pill tone="warning" withDot>
        não conclui
      </Pill>
    );
  }
  if (order.latenessS == null) return <span className="text-muted-foreground">sem prazo</span>;
  if (order.latenessS <= 0) {
    return (
      <Pill tone="success" withDot>
        no prazo
      </Pill>
    );
  }
  return (
    <Pill tone="danger" withDot>
      {`+${Math.ceil(order.latenessS / 60)} min`}
    </Pill>
  );
}

// Ordem a ordem do candidato escolhido: quando começa, quando termina, se atrasa e quanto de
// setup pagou. É o que transforma "esta sequência é melhor" em "por quê".
export function SequenceOrdersTable({ orders, labelOf, anchor }: Props) {
  if (orders.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Selecione uma sequência para ver a fila.</p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-left text-muted-foreground">
          <tr>
            <th className="py-1 pr-2 font-medium">Ordem</th>
            <th className="py-1 pr-2 font-medium">Início</th>
            <th className="py-1 pr-2 font-medium">Fim</th>
            <th className="py-1 pr-2 font-medium">Setup</th>
            <th className="py-1 font-medium">Prazo</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order, index) => (
            <tr key={order.orderId} className="border-t">
              <td className="py-1.5 pr-2">
                <span className="text-muted-foreground">{index + 1}.</span> {labelOf(order.orderId)}
              </td>
              <td className="py-1.5 pr-2 tabular-nums">{fmtClock(anchor, order.startS)}</td>
              <td className="py-1.5 pr-2 tabular-nums">{fmtClock(anchor, order.endS)}</td>
              <td className="py-1.5 pr-2 tabular-nums">
                {order.setupS > 0 ? `${Math.round(order.setupS / 60)} min` : "—"}
              </td>
              <td className="py-1.5">
                <LatenessCell order={order} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
